import DBMaster from '../database/dbMaster';

/**
 * Get documents from MongoDB collection
 */
export const getDocumentFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    const document = await collection.findOne(filter);
    
    return document;
  } catch (err) {
    console.error("ERROR in getDocumentFromDB:", err);
    return null;
  }
};

/**
 * Get multiple documents from MongoDB collection
 */
export const getDocumentsFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    const documents = await collection.find(filter).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getDocumentsFromDB:", err);
    return null;
  }
};

/**
 * Get documents with count limit from MongoDB collection
 */
export const getAllDocumentsWithCountFromDB = async (collectionName, filter, count = 10) => {
  try {
    
    const collection = await DBMaster.getCollection(collectionName);
    const documents = await collection.find(filter).sort({ _id: 1 }).limit(Number(count)).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getAllDocumentsWithCountFromDB:", err);
    return null;
  }
};

/**
 * Get random documents from MongoDB collection
 */
export const getRandomDocumentsByCountFromDB = async (collectionName, filter, count = 10) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    const documents = await collection.aggregate([
      { $match: filter },
      { $sample: { size: Number(count) } }
    ]).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getRandomDocumentsByCountFromDB:", err);
    return null;
  }
};

/**
 * Get random single document from MongoDB collection
 */
export const getRandomDocumentFromDB = async (collectionName, filter) => {
  try {
    const documents = await getRandomDocumentsByCountFromDB(collectionName, filter, 1);
    return documents?.length > 0 ? documents[0] : null;
  } catch (err) {
    console.error("ERROR in getRandomDocumentFromDB:", err);
    return null;
  }
};

/**
 * Get regex matches from MongoDB collection
 */
export const getRegexMatchesFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    const documents = await collection.find(filter).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getRegexMatchesFromDB:", err);
    return null;
  }
};

/**
 * Get documents by priority from MongoDB collection
 */
export const getDocumentsByPriorityFromDB = async (collectionName, filter, order, count) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    // Determine sort order (1 for ascending, -1 for descending)
    const sortOrder = order === 'asc' ? 1 : -1;
    const documents = await collection.find(filter).sort({ priority: sortOrder }).limit(Number(count)).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getDocumentsByPriorityFromDB:", err);
    return null;
  }
};

/**
 * Get documents ordered by weight from MongoDB collection
 */
export const getDocumentsOrderedByWeightFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    const documents = await collection.find(filter).sort({ weight: 1 }).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getDocumentsOrderedByWeightFromDB:", err);
    return null;
  }
};

/**
 * Get documents by priority airline with underscore from MongoDB collection
 */
export const getDocumentByPriorityAirlineWithUnderscoreFromDB = async (collectionName, count, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    // Extract parameters from filter
    const priorityAirlineWithUnderscore = filter;
    
    // Build query criteria matching API logic
    const queryFinal = {
      [priorityAirlineWithUnderscore]: { $exists: false },
    };
    
    // Optimize: Use MongoDB limit instead of fetching all and slicing
    const documents = await collection.find(queryFinal).limit(Number(count)).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getDocumentByPriorityAirlineWithUnderscoreFromDB:", err);
    return null;
  }
};

/**
 * Get top airline routes from MongoDB collection
 */
export const getTopAirlineRoutesFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    // Build query with $or condition for src_country_id and dest_country_id
    const queryFinal = {};
    const srcCountryId = filter.src_country_id;
    const destCountryId = filter.dest_country_id;
    
    if (srcCountryId || destCountryId) {
      queryFinal.$or = [];
      if (srcCountryId) queryFinal.$or.push({ src_country_id: srcCountryId });
      if (destCountryId) queryFinal.$or.push({ dest_country_id: destCountryId });
    }
    
    if (filter.airline_code) {
      queryFinal.airline_code = filter.airline_code;
    }
    
    const count = Number(filter.count) || 10;
    const documents = await collection.find(queryFinal).limit(count).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getTopAirlineRoutesFromDB:", err);
    return null;
  }
};

/**
 * Get top airline domestic routes from MongoDB collection
 */
export const getTopAirlineDomesticRoutesFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    // Extract parameters from filter
    const { src_country_id = "", dest_country_id = "", airline_code = "", count = 10 } = filter || {};
    
    // Build query with AND condition for domestic routes (matching API behavior)
    let queryFinal = {};
    const andCondition = {};
    
    // Assign values directly (matching API implementation)
    andCondition.src_country_id = src_country_id;
    andCondition.dest_country_id = dest_country_id;
    queryFinal = { ...queryFinal, ...andCondition };
    queryFinal.airline_code = airline_code;
    
    const documents = await collection.find(queryFinal).limit(Number(count)).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getTopAirlineDomesticRoutesFromDB:", err);
    return null;
  }
};

/**
 * Get IF routes from MongoDB collection
 */
export const getIfRoutesFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    // Parse sectors from filter
    const queryFinal = {};
    if (filter?.sectors) {
      try {
        queryFinal.$or = JSON.parse(filter.sectors);
      } catch (parseError) {
        console.error("Error parsing sectors:", parseError);
        return null;
      }
    }
    
    const documents = await collection.find(queryFinal).limit(100).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getIfRoutesFromDB:", err);
    return null;
  }
};

/**
 * Get flight cities for multiple codes from MongoDB collection
 */
export const getFlightCitiesForMultipleCodeFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    // Extract parameters from filter
    const { cityCodeSet, ...otherFilters } = filter || {};
    
    // Convert cityCodeSet to array if it's a string
    const cityCodesArray = Array.isArray(cityCodeSet) 
      ? cityCodeSet 
      : cityCodeSet.split(',');
    
    // Build query with $in operator for multiple flight city codes
    const query = {
      flight_city_code: { $in: cityCodesArray },
      ...otherFilters // Include any additional filters
    };
    
    const documents = await collection.find(query).limit(100).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getFlightCitiesForMultipleCodeFromDB:", err);
    return null;
  }
};

/**
 * Get international cities for multiple codes from MongoDB collection
 */
export const getIntlCitiesForMultipleCodeFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    // Extract parameters from filter
    const { cityCodeSet, ...otherFilters } = filter || {};
    
    // Convert cityCodeSet to array if it's a string
    const cityCodesArray = Array.isArray(cityCodeSet) 
      ? cityCodeSet 
      : cityCodeSet.split(',');
    
    // Build query with $in operator for multiple city codes
    const query = {
      city_code: { $in: cityCodesArray },
      ...otherFilters // Include any additional filters
    };
    
    const documents = await collection.find(query).limit(100).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getIntlCitiesForMultipleCodeFromDB:", err);
    return null;
  }
};

/**
 * Get international non-India routes from MongoDB collection
 */
export const getIntlNonIndiaRoutesFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    // Extract parameters from filter
    const { srcCityCode = "", excludeCityCodes = [], count = 10, ...otherFilters } = filter || {};
    
    // Convert excludeCityCodes to array if it's a string
    const excludeCodesArray = Array.isArray(excludeCityCodes) 
      ? excludeCityCodes 
      : excludeCityCodes.split(',');
    
    // Build MongoDB aggregation pipeline for filtering non-India routes
    const pipeline = [
      {
        $match: {
          src_city_code: srcCityCode,
          $and: [
            { dest_city_code: { $nin: excludeCodesArray } },
            { src_city_code: { $nin: excludeCodesArray } }
          ],
          ...otherFilters // Include any additional filters
        }
      },
      {
        $limit: Number(count)
      }
    ];
    
    const documents = await collection.aggregate(pipeline).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getIntlNonIndiaRoutesFromDB:", err);
    return null;
  }
};

/**
 * Get blocks with link target from MongoDB collection
 */
export const getBlocksWithLinkTargetFromDB = async (collectionName, filter) => {
  try {
    const collection = await DBMaster.getCollection(collectionName);
    if (!collection) {
      return null;
    }
    // Extract parameters from filter
    const { contentType = "", linkTargetUrl = "", ...otherFilters } = filter || {};
    // Build MongoDB aggregation pipeline for filtering blocks with link_target containing URL
    const pipeline = [
      {
        $match: {
          content_type: contentType,
          link_target: { 
            $exists: true, 
            $ne: "", 
            $regex: linkTargetUrl, 
            $options: "i" 
          },
          ...otherFilters // Include any additional filters
        }
      },
      {
        $sort: { weight: 1 }
      }
    ];
    
    const documents = await collection.aggregate(pipeline).toArray();
    
    return documents;
  } catch (err) {
    console.error("ERROR in getBlocksWithLinkTargetFromDB:", err);
    return null;
  }
};
